import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, type MeView, type SessionView } from './api.ts';
import { formatWhen } from './Users.tsx';
import './users.css';

/**
 * Your own account: your password, and every browser you are signed in on.
 *
 * A panel, not a page: it renders inside Settings, which owns the h1. It is
 * offered to everybody who can sign in, whatever their role, because neither of
 * these is an administrative action — they are the two things a person has to
 * be able to do about their own access without asking anyone.
 *
 * The session list is the only place in the console where somebody can see that
 * an account is signed in somewhere they do not recognise, and end it. That is
 * worth more than it looks: this console reaches several organisations'
 * security posture, and a session nobody can see is a session nobody revokes.
 */

const ROLE_TEXT: Record<string, string> = {
  owner: 'Owner',
  engineer: 'Engineer',
  viewer: 'Viewer',
};

const message = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

interface AccountProps {
  readonly me: MeView;
}

export const Account = ({ me }: AccountProps) => {
  const [announcement, setAnnouncement] = useState('');

  /**
   * A screen reader does not re-announce a live region whose text is unchanged.
   * The alternating zero-width space guarantees a change without a visible one.
   */
  const announce = useCallback((text: string) => {
    setAnnouncement((previous) => (previous.endsWith('\u200B') ? text : `${text}\u200B`));
  }, []);

  return (
    <section className="ac-panel" aria-labelledby="account-heading">
      <h2 id="account-heading">Your account</h2>
      {/*
        The timestamp is rendered as the shared formatter writes it. An earlier
        version lower-cased the sentence to make it flow and turned "9 Aug 2026"
        into "9 aug 2026", which reads as a bug in the product rather than as
        prose.
      */}
      <p className="lead">
        You are signed in as <strong>{me.displayName}</strong>
        {me.email !== null && <> ({me.email})</>}, with the{' '}
        {(ROLE_TEXT[me.role] ?? me.role).toLowerCase()} role.{' '}
        {/* The token has no account, so it has no sign-in history to report. */}
        {me.breakGlass
          ? ''
          : me.lastSignedInAt === null
            ? 'No earlier sign-in has been recorded for your account.'
            : `The last sign-in recorded for your account was ${formatWhen(me.lastSignedInAt)}.`}
      </p>

      <p aria-live="polite" role="status" className="sr-only">
        {announcement}
      </p>

      {/*
        The break-glass token is not an account. It has no password of its own
        and opens no session, so both forms below would answer 403. Saying so is
        the only useful thing this tab can do for that caller — and it is worth
        saying, because somebody using it should know they are not signed in as
        a person and that nothing they do here is attributable to one.
      */}
      {me.breakGlass ? (
        <div className="callout callout-caution">
          <span className="callout-title">This is the deployment&rsquo;s API token, not an account</span>
          It holds every capability and belongs to nobody, so there is no password to change and no
          session to end — closing the browser is the whole of signing out. Anything done with it is
          recorded against the token rather than against a person. Create yourself an account under
          People and use that instead.
        </div>
      ) : (
        <div className="ac-grid">
          <ChangePassword announce={announce} />
          <Sessions announce={announce} />
        </div>
      )}
    </section>
  );
};

interface PanelProps {
  readonly announce: (text: string) => void;
}

const ChangePassword = ({ announce }: PanelProps) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { revokedSessions } = await api.changePassword(current, next);
      // Neither value survives the request. Nothing here is logged, nothing is
      // put in a URL, and nothing is rendered back.
      setCurrent('');
      setNext('');
      setConfirm('');
      // The count comes from the server because only the server knows it, and
      // it is worth saying: changing a password because it may be known to
      // somebody else achieves nothing if the sessions opened with it stay
      // live, so the person needs to see that they did not.
      const said =
        revokedSessions > 1
          ? `Your password has been changed, and your other ${revokedSessions - 1} sessions were signed out.`
          : 'Your password has been changed. You had no other sessions to sign out.';
      setStatus(said);
      announce(said);
    } catch (cause) {
      const text = message(cause);
      setError(text);
      announce(`Your password was not changed. ${text}`);
      setCurrent('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card ac-card">
      <div className="card-head">
        <h3>Change your password</h3>
      </div>
      <div className="card-body">
        <form onSubmit={(event) => void submit(event)} noValidate>
          <div className="field">
            <label htmlFor="account-current">Current password</label>
            <input
              id="account-current"
              type="password"
              autoComplete="current-password"
              value={current}
              disabled={busy}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="account-new">New password</label>
            <input
              id="account-new"
              type="password"
              autoComplete="new-password"
              value={next}
              disabled={busy}
              aria-describedby="account-new-help"
              onChange={(event) => setNext(event.target.value)}
            />
            <span className="hint" id="account-new-help">
              The server sets the minimum length and will say so if this one is too short. There is
              no reset by email, so if you lose this an owner has to set you a new one.
            </span>
          </div>

          {/*
            A confirmation field, because there is no way back from a typo here.
            The new password is never echoed, the old one stops working the
            moment this succeeds, and nothing can send a reset link — so a
            mistyped character locks you out of your own console until another
            owner intervenes. This is the one field in the product that exists
            purely because the recovery path is a person.
          */}
          <div className="field">
            <label htmlFor="account-confirm">New password again</label>
            <input
              id="account-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              disabled={busy}
              aria-describedby="account-confirm-help"
              aria-invalid={confirm.length > 0 && confirm !== next ? true : undefined}
              onChange={(event) => setConfirm(event.target.value)}
            />
            <span className="hint" id="account-confirm-help">
              {confirm.length > 0 && confirm !== next
                ? 'These two do not match yet.'
                : 'Type it a second time. A typo here would lock you out.'}
            </span>
          </div>

          {error !== null && <div className="error ac-error">{error}</div>}
          {status !== null && <p className="status-inline ac-status">{status}</p>}

          <div className="toolbar">
            <button
              type="submit"
              className="btn btn-primary"
              aria-busy={busy || undefined}
              disabled={busy || current.length === 0 || next.length === 0 || confirm !== next}
            >
              {busy ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Sessions = ({ announce }: PanelProps) => {
  const [sessions, setSessions] = useState<SessionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { sessions: rows } = await api.listSessions();
      setSessions(rows);
      setError(null);
    } catch (cause) {
      setError(message(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (session: SessionView) => {
    setBusy(true);
    setError(null);
    try {
      await api.revokeSession(session.id);
      await load();
      announce('That session has been signed out.');
    } catch (cause) {
      const text = message(cause);
      setError(text);
      announce(`That session was not signed out. ${text}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card ac-card">
      <div className="card-head">
        <h3>Where you are signed in</h3>
      </div>
      <div className="card-body">
        <p className="note ac-lead">
          One entry per browser holding a live session. If you do not recognise one, sign it out —
          it stops working immediately, and whoever was using it has to know your password to come
          back.
        </p>

        {error !== null && <div className="error ac-error">{error}</div>}

        {loading ? (
          <p className="note">Loading…</p>
        ) : sessions.length === 0 ? (
          <p className="note">No sessions are listed.</p>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl ac-sessions">
              <thead>
                <tr>
                  <th scope="col">Where from</th>
                  <th scope="col">Signed in</th>
                  <th scope="col">Last used</th>
                  <th scope="col">Expires</th>
                  <th scope="col">Sign out</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.id}>
                    <th scope="row" className="ac-where">
                      <span className="mono">{session.ip ?? 'Address not recorded'}</span>
                      {/* Verbatim, and narrow: a user agent is raw client output
                          and guessing a browser name from it is guesswork the
                          console has no reason to publish as fact. */}
                      <span className="mono ac-agent">
                        {session.userAgent ?? 'Browser not recorded'}
                      </span>
                      {session.current && <span className="ac-current">This browser</span>}
                    </th>
                    <td className="num">{formatWhen(session.createdAt)}</td>
                    <td className="num">{formatWhen(session.lastSeenAt)}</td>
                    <td className="num">{formatWhen(session.expiresAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        disabled={busy || session.current}
                        title={
                          session.current
                            ? 'This is the session you are using. Use Sign out in the sidebar to end it.'
                            : undefined
                        }
                        onClick={() => void revoke(session)}
                      >
                        Sign out
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
